/**
 * Player preferences: where they live, when they are written, and who is told.
 *
 * Three copies of the same values exist and they are not equal in authority:
 *
 * 1. **The server** owns them, so a player's volumes follow their account to a
 *    second device. It is also the only one that clamps.
 * 2. **localStorage** is a cache, read synchronously at boot. Without it the
 *    first seconds of a session play at the default mix regardless of what the
 *    player set, because the fetch has not landed yet — and the one setting
 *    people care most about getting right immediately is volume.
 * 3. **The subscribers** are the live engine: the audio buses, the camera, the
 *    HUD. They are pushed to on every change so nothing has to poll.
 *
 * Writes are debounced. A volume slider fires an `input` event per pixel of
 * drag; sending one PUT each would be a hundred requests to set one number.
 */

import { api } from '../../../core/network/api';
import { readMusicPreference, setMusicEnabled as writeMusicPreference } from '../../audio/music-preference';
import {
  coercePreferences,
  defaultPreferences,
  type AudioSettings,
  type GameplaySettings,
  type PlayerPreferences,
} from './settings-types';

export const PREFERENCES_STORAGE_KEY = 'tallow.settings.preferences';

/** Long enough to swallow a slider drag, short enough that it feels saved. */
export const SAVE_DEBOUNCE_MS = 600;

type Listener = (prefs: PlayerPreferences) => void;

function readCache(): PlayerPreferences {
  try {
    const raw = window.localStorage.getItem(PREFERENCES_STORAGE_KEY);
    const prefs = raw ? coercePreferences(JSON.parse(raw)) : defaultPreferences();
    // The shared on/off key is the newer word when the two disagree: the
    // landing page and the header button write only it. Without this, the
    // store's boot copy (default: on) would switch the bed back on now that
    // the engine follows the store (2026-10-15).
    const shared = readMusicPreference();
    if (shared !== null) prefs.audio.musicEnabled = shared;
    return prefs;
  } catch {
    // Private mode throws on access, and a corrupt blob throws on parse. Either
    // way the defaults are a correct answer and a thrown getter is not.
    return defaultPreferences();
  }
}

function writeCache(prefs: PlayerPreferences): void {
  try {
    window.localStorage.setItem(PREFERENCES_STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // Unpersisted is still applied for this session.
  }
}

export class SettingsStore {
  private prefs: PlayerPreferences = readCache();
  private listeners = new Set<Listener>();
  private saveTimer: ReturnType<typeof setTimeout> | null = null;
  private inFlight: Promise<unknown> | null = null;
  /** Something has changed since the last successful write. */
  private dirty = false;

  /** The current values. Never a live reference — callers must not mutate them. */
  get(): PlayerPreferences {
    return { audio: { ...this.prefs.audio }, gameplay: { ...this.prefs.gameplay } };
  }

  /**
   * Subscribe, and get the current values immediately.
   *
   * The immediate call is the point: a subscriber that only hears about
   * *changes* would run at the default mix until the player touched a slider.
   */
  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.get());
    return () => this.listeners.delete(fn);
  }

  private emit(): void {
    const snapshot = this.get();
    this.listeners.forEach((fn) => fn(snapshot));
  }

  /** Adopt server values without echoing them straight back as a save. */
  hydrate(prefs: PlayerPreferences): void {
    this.prefs = coercePreferences(prefs);
    writeCache(this.prefs);
    this.syncMusicPreference();
    this.emit();
  }

  patchAudio(patch: Partial<AudioSettings>): void {
    this.prefs = coercePreferences({ ...this.prefs, audio: { ...this.prefs.audio, ...patch } });
    this.afterChange();
  }

  patchGameplay(patch: Partial<GameplaySettings>): void {
    this.prefs = coercePreferences({ ...this.prefs, gameplay: { ...this.prefs.gameplay, ...patch } });
    this.afterChange();
  }

  private afterChange(): void {
    this.dirty = true;
    writeCache(this.prefs);
    this.syncMusicPreference();
    this.emit();
    this.scheduleSave();
  }

  /**
   * The header's music toggle and the landing page both read
   * `tallow.audio.music`. Keeping that key in step means switching music off in
   * settings also switches off the button, and vice versa, rather than leaving
   * two controls that disagree about the same bed.
   */
  private syncMusicPreference(): void {
    writeMusicPreference(this.prefs.audio.musicEnabled);
  }

  private scheduleSave(): void {
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => {
      this.saveTimer = null;
      void this.flush();
    }, SAVE_DEBOUNCE_MS);
  }

  /**
   * Send now. Called on close, so a modal dismissed inside the debounce still
   * saves — and a no-op when nothing has changed since the last write, so
   * opening and closing the screen does not cost a request.
   */
  async flush(): Promise<void> {
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }
    if (!this.dirty) return;
    this.dirty = false;
    const body = this.get();
    // Serialise: two overlapping PUTs can land out of order, and the loser
    // would be the newer one.
    const send = (this.inFlight ?? Promise.resolve())
      .catch(() => undefined)
      .then(() => api.put<PlayerPreferences>('/settings/preferences', body))
      .then((saved) => {
        // The server clamps. Adopt what it stored so the sliders cannot show a
        // value the account does not actually have.
        this.prefs = coercePreferences(saved);
        writeCache(this.prefs);
        this.emit();
      })
      .catch(() => {
        // Offline, or signed out. The local copy still applies; mark it unsent
        // so the next change retries this state rather than dropping it.
        this.dirty = true;
      });
    this.inFlight = send;
    await send;
  }
}

/** One store per tab. The engine and the modal must be looking at the same object. */
export const settingsStore = new SettingsStore();
